/**
 * Alpine.js Inline Comments Component
 *
 * Handles expandable inline comment sections in the feed.
 * Loads comments via AJAX when expanded, shows comment form and recent comments.
 *
 * Usage in Twig:
 * <div x-data="inlineComments('post', '123', '456', 5)">
 *   <button @click="toggle()">Comments</button>
 *   <div x-show="isExpanded">...</div>
 * </div>
 */

export function inlineComments(entityType, entityId, feedOwnerId, commentCount) {
    return {
        entityType,
        entityId: String(entityId),
        feedOwnerId: String(feedOwnerId),
        commentCount: parseInt(commentCount) || 0,
        isExpanded: false,
        isLoading: false,
        commentsHtml: '',
        hasLoaded: false,
        canComment: null,

        toggle() {
            this.isExpanded = !this.isExpanded;

            if (this.isExpanded && !this.hasLoaded) {
                this.loadComments();
            }
        },

        async loadComments() {
            if (this.isLoading) return;
            this.isLoading = true;

            try {
                const params = new URLSearchParams({
                    entity_type: this.entityType,
                    entity_id: this.entityId,
                    feed_owner_id: this.feedOwnerId,
                    limit: '3'
                });

                const response = await fetch(`/app/comment/fetch?${params}`);
                if (!response.ok) {
                    throw new Error('Failed to load comments');
                }

                const html = await response.text();

                const tmp = document.createElement('div');
                tmp.innerHTML = html;
                const commentList = tmp.querySelector('[data-can-comment]');
                if (commentList) {
                    this.canComment = commentList.dataset.canComment === '1';
                } else {
                    this.canComment = true;
                }

                this.commentsHtml = html;
                this.hasLoaded = true;

                // Initialize Lucide icons in loaded content
                this.$nextTick(() => {
                    const container = this.$refs.commentsContainer;
                    if (container && window.LucideIcons) {
                        window.LucideIcons.initialize(container);
                    }
                });
            } catch (error) {
                console.error('Error loading comments:', error);
                this.canComment = false;
                window.showError?.('Failed to load comments');
            } finally {
                this.isLoading = false;
            }
        },

        onCommentAdded(html) {
            // Prepend new comment to the list
            const container = this.$refs.commentsContainer;
            if (container) {
                const commentList = container.querySelector('.comment-list');
                if (commentList) {
                    commentList.classList.remove('hidden');
                    commentList.insertAdjacentHTML('afterbegin', html);
                } else {
                    // No comment list yet, create one
                    container.innerHTML = `<div class="comment-list space-y-6">${html}</div>`;
                }
            }
            this.commentCount++;
        },

        incrementCount() {
            this.commentCount++;
        },

        decrementCount() {
            this.commentCount = Math.max(0, this.commentCount - 1);
        }
    };
}

if (typeof window !== 'undefined') {
    window.inlineComments = inlineComments;
}

export default inlineComments;
